import { makeAutoObservable, runInAction } from 'mobx';
import { axiosInstance } from '@/utils/axiosConfig';
import { jwtDecode }  from 'jwt-decode';
import i18next from 'i18next';

export interface User {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  organizations: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
    logo?: string;
  }>;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
  logo?: string;
}

interface JwtPayload {
  exp: number;
  userId: string;
  email?: string;
}

class AuthStoreClass {
  accessToken: string | null = localStorage.getItem('accessToken');
  refreshToken: string | null = localStorage.getItem('refreshToken');
  user: User | null = null;
  error: string | null = null;
  loading = false;
  currentOrganization: Organization | null = null;

  constructor() {
    makeAutoObservable(this);
    this.initializeFromStorage();
  }

  private async initializeFromStorage() {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        await this.fetchUserData();
        // Set first organization as current if none is selected
        if (!this.currentOrganization && this.user?.organizations.length) {
          this.setCurrentOrganization(this.user.organizations[0]);
        }
      } catch (error) {
        console.error('Failed to initialize from storage:', error);
        this.logout();
      }
    }
  }

  setCurrentOrganization(organization: Organization | null) {
    this.currentOrganization = organization;
    if (organization) {
      localStorage.setItem('lastOrganizationId', organization.id);
    } else {
      localStorage.removeItem('lastOrganizationId');
    }
  }

  setTokens(accessToken: string) {
    this.accessToken = accessToken;
    localStorage.setItem('accessToken', accessToken);
    
    // Set the token in axios headers immediately
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  setUser(user: User | null) {
    runInAction(() => {
      this.user = user;
    });
  }

  setError(error: string | null) {
    runInAction(() => {
      this.error = error;
    });
  }

  setLoading(loading: boolean) {
    runInAction(() => {
      this.loading = loading;
    });
  }

  get isTokenExpired(): boolean {
    if (!this.accessToken) return true;
    
    try {
      const decoded = jwtDecode<JwtPayload>(this.accessToken);
      const currentTime = Date.now() / 1000;
      
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  }

  get isAuthenticated(): boolean {
    return !!this.accessToken && !!this.user;
  }

  async login(email: string, password: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await axiosInstance.post('/auth/login', { email, password });
      const { accessToken, user } = response.data;
      
      this.setTokens(accessToken);
      this.setUser(user);
      await this.setInitialOrganization();
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || i18next.t('errors.loginFailed', 'Login failed. Please check your credentials.');
      this.setError(message);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  googleLogin() {
    window.location.href = `${import.meta.env.VITE_API_URL}/auth/google`;
  }

  microsoftLogin() {
    window.location.href = `${import.meta.env.VITE_API_URL}/auth/microsoft`;
  }

  async logout() {
    try {
      // First clear axios headers to prevent further authenticated requests
      delete axiosInstance.defaults.headers.common['Authorization'];

      // Call backend logout endpoint
      await axiosInstance.post('/auth/logout', {}, { 
        withCredentials: true,
        validateStatus: (status) => status < 500
      });

      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('lastOrganizationId');
      
      // Clear store state
      this.accessToken = null;
      this.user = null;
      this.currentOrganization = null;

      // Wait a small delay to ensure cookie deletion is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Only then redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, clear local state and redirect
      this.accessToken = null;
      this.user = null;
      this.currentOrganization = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('lastOrganizationId');
      delete axiosInstance.defaults.headers.common['Authorization'];
      
      await new Promise(resolve => setTimeout(resolve, 100));
      window.location.href = '/login';
    }
  }

  async fetchUserData() {
    try {
      const response = await axiosInstance.get('/auth/me');
      this.user = response.data;
      
      // Restore or set current organization
      const storedOrg = localStorage.getItem('currentOrganization');
      if (storedOrg) {
        const parsedOrg = JSON.parse(storedOrg);
        // Verify the stored org is still valid
        const validOrg = this.user.organizations.find(org => org.id === parsedOrg.id);
        if (validOrg) {
          this.currentOrganization = validOrg;
        } else {
          // If stored org is invalid, set first available org
          this.currentOrganization = this.user.organizations[0] || null;
          if (this.currentOrganization) {
            localStorage.setItem('currentOrganization', JSON.stringify(this.currentOrganization));
          }
        }
      } else if (this.user.organizations.length > 0) {
        // If no stored org, set first available org
        this.currentOrganization = this.user.organizations[0];
        localStorage.setItem('currentOrganization', JSON.stringify(this.currentOrganization));
      }

      this.setError(null);
    } catch (error: any) {
      this.setError(error.response?.data?.message || 'Failed to fetch user data');
      throw error;
    }
  }

  async updateName(name: string) {
    try {
      await axiosInstance.put('/auth/update-name', { name });
      if (this.user) {
        this.user.name = name;
      }
    } catch (error) {
      console.error('Failed to update name:', error);
      throw error;
    }
  }

  async updateOrganizationName(orgId: string, name: string, slug: string) {
    try {
      await axiosInstance.put(`/organizations/${orgId}`, { name, slug });
      if (this.user) {
        const org = this.user.organizations.find(o => o.id === orgId);
        if (org) {
          org.name = name;
          org.slug = slug;
        }
      }
    } catch (error) {
      console.error('Failed to update organization:', error);
      throw error;
    }
  }

  async sendVerificationEmail() {
    try {
      if (!this.user?.email) {
        throw new Error('No email address available');
      }

      const response = await axiosInstance.post('/auth/send-verification', {
        email: this.user.email
      });

      runInAction(() => {
        this.error = null;
      });

      return response.data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to send verification email';
      });
      throw error;
    }
  }

  async verifyEmail(code: string) {
    try {
      if (!this.user?.email) {
        throw new Error('No email address available');
      }

      const response = await axiosInstance.post('/auth/verify-email', {
        email: this.user.email,
        code
      });

      runInAction(() => {
        if (response.data.message === 'Email verified successfully') {
          if (this.user) {
            this.user.emailVerified = true;
          }
        }
        this.error = null;
      });

      return response.data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to verify email';
      });
      throw error;
    }
  }

  async updatePassword(currentPassword: string, newPassword: string) {
    try {
      await axiosInstance.put('/auth/update-password', {
        currentPassword,
        newPassword
      });
      this.setError(null); // Clear any previous errors
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update password';
      this.setError(errorMessage);
      throw error;
    }
  }

  async setInitialPassword(password: string) {
    try {
      await axiosInstance.post('/auth/set-password', { 
        newPassword: password 
      });
      this.setError(null); // Clear any previous errors
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to set password';
      this.setError(errorMessage);
      throw error;
    }
  }

  async sendOrganizationInvite(orgId: string, email: string) {
    try {
      await axiosInstance.post(`/organizations/${orgId}/invites`, { email });
      this.setError(null);
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to send invitation';
      this.setError(errorMessage);
      throw error;
    }
  }

  async generateOrganizationInviteLink(orgId: string, email: string) {
    try {
      const response = await axiosInstance.post(`/organizations/${orgId}/invite-link`, { email });
      if (!response.data.emailSent) {
        this.setError('Invite link generated, but email delivery failed');
      }
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to generate invite link';
      this.setError(errorMessage);
      throw error;
    }
  }

  async verifyOrganizationInvite(token: string) {
    try {
      const response = await axiosInstance.get(`/organizations/invites/${token}/verify`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to verify invitation';
      this.setError(errorMessage);
      throw error;
    }
  }

  async acceptOrganizationInvite(token: string) {
    try {
      const response = await axiosInstance.post(`/organizations/invites/${token}/accept`);
      if (this.user) {
        this.user.organizations = response.data.organizations;
      }
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to accept invitation';
      this.setError(errorMessage);
      throw error;
    }
  }

  async register(email: string, password: string, name: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await axiosInstance.post('/auth/register', {
        email,
        password,
        name
      });
      const { accessToken, user } = response.data;
      
      this.setTokens(accessToken);
      this.setUser(user);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      this.setError(message);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async leaveOrganization(orgId: string) {
    try {
      await axiosInstance.post(`/organizations/${orgId}/leave`);
      if (this.user) {
        this.user.organizations = this.user.organizations.filter(org => org.id !== orgId);
      }
      this.setError(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to leave organization';
      this.setError(errorMessage);
      throw error;
    }
  }

  async setInitialOrganization() {
    if (this.user?.organizations && this.user.organizations.length > 0) {
      // Get the last used organization from localStorage
      const lastOrgId = localStorage.getItem('lastOrganizationId');
      
      if (lastOrgId) {
        // Find the organization in user's organizations
        const org = this.user.organizations.find(org => org.id === lastOrgId);
        if (org) {
          this.setCurrentOrganization(org);
          return;
        }
      }
      
      // If no last org or it wasn't found, use the first one
      this.setCurrentOrganization(this.user.organizations[0]);
    }
  }

  async handleSSOCallback(token: string) {
    try {
      this.setLoading(true);
      this.setTokens(token);
      await this.fetchUserData();
      await this.setInitialOrganization();
    } catch (error: any) {
      this.handleError(error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  handleError(error: any) {
    const message = error.response?.data?.message || 'An error occurred. Please try again later.';
    this.setError(message);
  }

  async updateOrganizationLogo(orgId: string, file: File) {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await axiosInstance.post(`/organizations/${orgId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (this.user) {
        const org = this.user.organizations.find(o => o.id === orgId);
        if (org) {
          org.logo = response.data.logoUrl;
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to update organization logo:', error);
      throw error;
    }
  }

  async deleteOrganizationLogo(orgId: string) {
    try {
      await axiosInstance.delete(`/organizations/${orgId}/logo`);
      
      // Update local state
      if (this.user) {
        const org = this.user.organizations.find(o => o.id === orgId);
        if (org) {
          org.logo = null;
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Failed to delete organization logo:', error);
      throw error;
    }
  }
}

export const authStore = new AuthStoreClass();
export type { User, Organization }; 